/**
 * The one per-user time zone (IANA name), stored in `user_preferences` under
 * 'timezone'. Defaults to Eastern. Set from Settings > Display and used
 * everywhere personal: Rhythms' "today", the auto theme schedule (synced in
 * ThemeProvider) and notification quiet hours (mirrored to
 * user_notification_preferences.quiet_timezone, which the server reads).
 */
import { useCallback } from 'react'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/stores'
import { useUserPreference } from './use-user-preference'

export const DEFAULT_TIMEZONE = 'America/New_York'

export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
]

export function useUserTimezone() {
  const userId = useAuthStore((s) => s.user?.id)
  const { value, setValue, isLoaded } = useUserPreference<string>('timezone', DEFAULT_TIMEZONE)
  const setTimezone = useCallback(
    (tz: string) => {
      setValue(tz)
      // No row yet = quiet hours off; the notification page writes the zone on first save.
      if (userId) {
        void supabase
          .from('user_notification_preferences')
          .update({ quiet_timezone: tz })
          .eq('user_id', userId)
          .then(({ error }) => { if (error) console.warn('quiet_timezone sync failed', error) })
      }
    },
    [setValue, userId],
  )
  return { timezone: value || DEFAULT_TIMEZONE, setTimezone, isLoaded }
}

/** Today's date (YYYY-MM-DD) and weekday (0=Sun..6=Sat) in the given zone. */
export function todayInTimezone(timezone: string): { date: string; dow: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  }).formatToParts(new Date())
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(get('weekday'))
  return { date: `${get('year')}-${get('month')}-${get('day')}`, dow }
}
